export const ROUTES = {
  Root: "",
  Login: "/login",
  Signup: "/signup",
  Onboarding: "/onboarding",
  Profile: {
    Root: "profile",
    Children: {
      Overview: "overview",
      AccountInfo: "account-info",
      ServiceProfile: {
        Root: "service-profile",
        Children: {
          create: ":type/create",
          edit: ":type/:id",
          Type: ":type",
        },
      },
      ChangePhoneNumber: "change-phone-number",
      NotificationSettings: "notification-settings",
      Invoices: {
        Root: "invoices",
        Children: {
          Paid: "paid",
          Unpaid: "unpaid",
          SelectPayment: "select-payment",
        },
        Detail: "invoices/:id",
        InvoicePdf: "invoices/:id/view-invoice-pdf",
        ReceiptPdf: "invoices/:id/view-receipt-pdf",
        AccountStatement: "invoices/view-account-statement-pdf",
      },
      Payment: "payment",
      AddCreditCard: "add-credit-card",
      TermsOfServices: "terms-of-services",
      PrivacyPolicy: "privacy-policy",
      CreditAccount: {
        Root: "credit-account",
        Children: {
          General: "general",
          Massage: "massage",
          Beauty: "beauty",
          Pet: "pet",
        },
      },
      CreditAccountTransaction: "credit-account/transaction",
      MyVouchers: "my-vouchers",
      AccountMyReferral: "account-my-referral",
      AccountMyReferralHistory: "account-my-referral/history",
      AccountMyReferralMainCredit: "account-my-referral/main-credit",
      //Address page related
      Addresses: "addresses",
      Address: "addresses/:id",
      NewAddress: "addresses/new",
      //contact page related
      Contacts: "contacts",
      Contact: "contacts/:id",
      NewContact: "contacts/new",
      //packages page related
      Packages: "packages",
      Package: "packages/:id",
    },
  },
  ServiceDetail: "/service-detail/:name",
  SelectService: "/select-service/:name",
  Booking: {
    Root: "/booking/:name",
    SelectSlot: "booking/:name/select-slot",
    BookingInfo: "booking/:name/booking-info",
    Confirmation: "booking/:name/confirmation",
    ReviewConfirmation: "booking/:name/booking-review-confirmation",
    CompleteBooking: "booking/:name/complete-booking",
  },
  Visits: {
    Root: "visits",
    Children: {
      Upcoming: "upcoming",
      History: "history",
    },
    Rate: "rate",
    ManageVisit: "manage-visit",
    AirconReschedule: "aircon-reschedule",
    RescheduleVisit: "/visits/reschedule",
  },
  Rewards: {
    Root: "rewards",
    Children: {
      Promos: "promos",
      YourRewards: "your-rewards",
    },
  },
  Notifications: {
    Root: "notifications",
    Children: {
      Detail: ":id",
    },
  },
  DownloadApp: "download-app",
  CsatRate: "csat-rate",
} as const;

type RouteValue =
  | string
  | {
      Root?: string;
      Children?: Record<string, RouteValue>;
      [key: string]: RouteValue | undefined;
    };

const flattenRoutes = (routes: RouteValue, basePath: string = ""): string[] => {
  const paths: string[] = [];

  if (typeof routes === "string") {
    if (basePath) {
      const routeSegment = routes.startsWith("/") ? routes.slice(1) : routes;
      paths.push(`${basePath}/${routeSegment}`);
    } else {
      const routePath = routes.startsWith("/") ? routes : `/${routes}`;
      paths.push(routePath);
    }
    return paths;
  }

  if (typeof routes === "object" && routes !== null) {
    const root = routes.Root;
    const children = routes.Children;

    let currentBase = basePath;
    if (root !== undefined && typeof root === "string") {
      if (basePath) {
        const rootSegment = root.startsWith("/") ? root.slice(1) : root;
        currentBase = `${basePath}/${rootSegment}`;
      } else {
        currentBase = root.startsWith("/") ? root : `/${root}`;
      }
      paths.push(currentBase);
    }

    if (children && typeof children === "object") {
      Object.values(children).forEach((child) => {
        if (child !== undefined) {
          const childPaths = flattenRoutes(child, currentBase);
          paths.push(...childPaths);
        }
      });
    }

    Object.entries(routes).forEach(([key, value]) => {
      if (key !== "Root" && key !== "Children" && value !== undefined) {
        const nestedPaths = flattenRoutes(value, basePath);
        paths.push(...nestedPaths);
      }
    });
  }

  return paths;
};
const allPaths = flattenRoutes(ROUTES);

export const routePathExists = (path: string) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return allPaths.includes(normalizedPath);
};
